import {
  memo,
  MutableRefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Tooltip from "react-bootstrap/Tooltip";

import Bugsnag from "@bugsnag/js";
import { serializer } from "models";
import { twMerge } from "tailwind-merge";
import { useLocalStorage, useToggle } from "usehooks-ts";

import { faCommentDots } from "@fortawesome/free-regular-svg-icons/faCommentDots";
import { faSpinner } from "@fortawesome/free-solid-svg-icons/faSpinner";

import ContentEditor from "components/ContentEditor";
import ErrorBoundary from "components/ErrorBoundary";
import FontAwesomeIcon from "components/FontAwesomeIcon";
import useNotification from "components/Notification/useNotification";
import ProfileSnippet from "components/ProfileSnippet";
import SwitchButton from "components/SwitchButton";
import useAuthentication from "hooks/useAuthentication";
import { useEffectOnce } from "hooks/useEffectOnce";
import { useGlobalState } from "hooks/useGlobalState";
import { fetchApi } from "utils/baseFetcher";
import htmlToText from "utils/htmlToText";

import DiscussionEditorV3 from "./v3";

const MENTIONS_REGEX =
  /(data-smartkarma-citation=["'](insights|entities|accounts|companies):(\d+)["'])/gi;
const MENTION_REGEX =
  /data-smartkarma-citation=["'](insights|entities|accounts|companies):(\d+)["']/i;

const DiscussionEditor = ({
  comment,
  parent,
  commentable: inputCommentable,
  afterSubmit,
  onCancel,
  hideCommenter,
  style = "default",
  editorRef: propEditorRef,
  onChange,
  onError,
  smartSummary,
  bypassFocus,
  className,
  blurClassName,
  blurAfterSubmit,
  placeholder,
  onlyAttachments,
}: {
  comment?: any;
  parent?: any;
  commentable?: any;
  afterSubmit?: (
    _response: any,
    _otherParams: { usedSuggestion: boolean; usedSuggestionIndex?: number }
  ) => void;
  onCancel?: () => void;
  hideCommenter?: boolean;
  style?: "default" | "v2" | "v3";
  editorRef?: MutableRefObject<any>;
  onChange?: (_value: string) => void;
  onSuggesctionClick?: (_suggestion: string) => void;
  onError?: (_error: Error) => void;
  smartSummary?: any;
  bypassFocus?: boolean;
  className?: string;
  blurClassName?: string;
  editorClassName?: string;
  blurAfterSubmit?: boolean;
  placeholder?: string;
  onlyAttachments?: boolean;
}) => {
  const isSentimentGenerated = useRef<boolean>(false);
  const internalEditorRef = useRef<any>();
  const editorRef = propEditorRef || internalEditorRef;

  const { account: loggedInAccount } = useAuthentication();
  const { setNotification } = useNotification();
  const [cachedValue, setCachedValue] = useLocalStorage(
    `discussion-editor#${inputCommentable?.id}#${parent?.id}#${comment?.id}`,
    ""
  );
  const [cachedUsedSuggesstionIndex, setCachedUsedSuggestionIndex] =
    useLocalStorage(
      `discussion-editor-suggestion-index#${inputCommentable?.id}#${parent?.id}#${comment?.id}`,
      ""
    );
  const [cachedDraft, setCachedDraft] = useLocalStorage<{
    sentiment?: "bullish" | "bearish" | "neutral" | null;
    mode?: "polling" | "prediction" | null;
    targetPrice?: any;
    targetDate?: any;
    pollQuestion?: string | null;
    pollAllowMultipleSelections?: boolean;
    pollOptions?: string[];
    pollClosesAt?: string | null;
    attachments?: any[];
  }>(
    `discussion-editor-draft#${inputCommentable?.id}#${parent?.id}#${comment?.id}`,
    {}
  );
  const [value, setValue] = useState<string>(comment?.body || cachedValue);
  const textValue = useMemo(() => htmlToText(value)?.trim(), [value]);

  const [usedSuggestions, setUsedSuggestions] = useGlobalState(
    `discussion-editor-used-suggestion-indices#${inputCommentable?.id}`,
    []
  );

  const suggestions = useMemo(
    () =>
      (smartSummary?.suggestedQuestions as string[])
        ?.map((suggestion: string, index: number) => ({ suggestion, index }))
        ?.filter(
          ({ suggestion, index }) =>
            !value?.includes(suggestion) &&
            !usedSuggestions.includes(index) &&
            !smartSummary?.usedSuggestedQuestions?.includes(index)
        ),
    [
      smartSummary?.suggestedQuestions,
      smartSummary?.usedSuggestedQuestions,
      usedSuggestions,
      value,
    ]
  );

  const [attachments, setAttachments] = useState<any[]>(
    comment?.attachments || []
  );
  const [isAnonymous, toggleAnonymous] = useToggle(comment?.anonymize);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [focused, setFocused] = useState(
    (!!textValue || !suggestions?.length) && !bypassFocus
  );

  const [usedSuggestionIndex, setUsedSuggestionIndex] = useState<
    number | undefined
  >(
    cachedUsedSuggesstionIndex
      ? parseInt(cachedUsedSuggesstionIndex)
      : undefined
  );
  const usedSuggestion = useMemo(
    () => typeof usedSuggestionIndex !== "undefined" && !!textValue,
    [textValue, usedSuggestionIndex]
  );

  const mentionedMatches = useMemo(() => value.match(MENTIONS_REGEX), [value]);
  const [sentiment, setSentiment] = useState<
    "bullish" | "bearish" | "neutral" | null
  >(comment?.sentiment ?? cachedDraft?.sentiment ?? null);

  const initialMode = cachedDraft?.mode ?? null;
  const [mode, setMode] = useState<"polling" | "prediction" | null>(
    initialMode
  );
  const prevModeRef = useRef<"polling" | "prediction" | null>(initialMode);

  const [targetPrice, setTargetPrice] = useState(
    cachedDraft?.targetPrice ?? null
  );
  const [targetDate, setTargetDate] = useState(cachedDraft?.targetDate ?? null);

  const [pollQuestion, setPollQuestion] = useState<string | null>(
    cachedDraft?.pollQuestion ?? null
  );
  const [pollAllowMultipleSelections, setPollAllowMultipleSelections] =
    useState(cachedDraft?.pollAllowMultipleSelections ?? false);
  const [pollOptions, setPollOptions] = useState<string[]>(
    cachedDraft?.pollOptions ?? []
  );
  const [pollClosesAt, setPollClosesAt] = useState<string | null>(
    cachedDraft?.pollClosesAt ?? null
  );

  const [taggedEntity, setTaggedEntity] = useState(null);
  const [taggedInsights, setTaggedInsights] = useState<any>(null);

  const [entityPrice, setEntityPrice] = useState<any>(null);

  const commentable = useMemo(
    () =>
      inputCommentable ||
      comment?.commentable ||
      parent?.commentable ||
      taggedEntity,
    [comment?.commentable, inputCommentable, parent?.commentable, taggedEntity]
  );

  const commentableInsights = useMemo(() => {
    const mentioned =
      mentionedMatches
        ?.filter((match) => match.includes("insights:"))
        ?.map((match) => ({
          type: "insights",
          id: match.match(MENTION_REGEX)?.[2],
        })) || [];

    return mentioned;
  }, [mentionedMatches]);

  const commentableEntities = useMemo(() => {
    const entities =
      mentionedMatches
        ?.filter((match) => match.includes("entities:"))
        ?.map((match) => ({
          type: "entities",
          id: match.match(MENTION_REGEX)?.[2],
        })) || [];
    if (["insights", "Insight"].includes(commentable?.type)) {
      if (
        commentable.primaryEntity &&
        !entities.some((e) => e.id === commentable.primaryEntity.id)
      ) {
        entities.push({
          type: "entities",
          id: commentable.primaryEntity.id,
        });
      }
      if (
        commentable["primary-entity"] &&
        !entities.some((e) => e.id === commentable["primary-entity"].id)
      ) {
        entities.push({
          type: "entities",
          id: commentable["primary-entity"].id,
        });
      }
    }

    if (taggedInsights) {
      if (
        taggedInsights?.primaryEntity &&
        !entities.some((e) => e.id === taggedInsights?.primaryEntity?.id)
      ) {
        entities.push({
          type: "entities",
          id: taggedInsights?.primaryEntity?.id,
        });
      }
    }

    return entities.filter((item) => item.id !== commentable?.id);
  }, [commentable, mentionedMatches, taggedInsights]);

  const canSubmit = useMemo(() => {
    const hasAttachments = !!attachments.length;
    const baseCondition = !!commentable || parent;

    if (mode === "polling") {
      const validOptions = pollOptions.filter(Boolean);
      return (
        baseCondition &&
        !!textValue &&
        validOptions.length >= 2 &&
        !!pollClosesAt
      );
    } else if (mode === "prediction") {
      return baseCondition && !!targetDate && !!targetPrice;
    }

    return baseCondition && (!!textValue || hasAttachments);
  }, [
    attachments.length,
    commentable,
    mode,
    parent,
    pollClosesAt,
    pollOptions,
    targetDate,
    targetPrice,
    textValue,
  ]);

  const submit = useCallback(async () => {
    if (isSubmitting) return;

    try {
      setIsSubmitting(true);

      const commentData: any = {
        id: comment?.id,
        body: value || "<p></p>",
        parent,
        anonymize: isAnonymous,
        commenter: isAnonymous ? null : loggedInAccount,
        sentiment,

        fromSuggestedQuestion: usedSuggestion,
        questionIndex: usedSuggestionIndex,

        commentable: commentable || parent?.commentable,

        attachments: attachments.map((attachment) => ({
          id: attachment.id,
          fileName:
            attachment.name ||
            attachment.sourceFileName ||
            attachment["source-file-name"],
          source: attachment.source,
        })),
      };

      if (commentableInsights.length) {
        commentData.commentableInsights = commentableInsights;
      }

      if (commentableEntities.length) {
        commentData.commentableEntities = commentableEntities;
      }

      if (taggedInsights) {
        commentData.primaryInsight = taggedInsights;
      }

      if (mode === "prediction" && commentable) {
        if (!targetDate || !targetPrice) {
          setNotification(
            "Please enter both target date and target price for prediction",
            "error"
          );
          return;
        }
        commentData.commentPredictionAttributes = {
          targetPrice,
          targetDate: new Date(targetDate).toISOString(),
          sentiment:
            sentiment || !entityPrice?.price
              ? "bullish"
              : entityPrice?.price < targetPrice
              ? "bullish"
              : "bearish",
        };
      }

      if (mode === "polling") {
        const validOptions = pollOptions.filter(Boolean);
        if (!textValue.length || validOptions.length < 2) {
          setNotification(
            "Please enter a question and at least 2 options for the poll",
            "error"
          );
          return;
        }
        commentData.commentPollAttributes = {
          question: `${
            textValue.length > 70 ? `${textValue.slice(0, 70)}...` : textValue
          }`,
          allowMultipleSelections: pollAllowMultipleSelections,
          closesAt: pollClosesAt ? new Date(pollClosesAt).toISOString() : null,
          commentPollOptionsAttributes: validOptions.map((label, position) => ({
            label,
            position,
          })),
        };
      }

      if (entityPrice?.price) {
        commentData.entitySnapshot = {
          price: entityPrice.price,
          source: entityPrice.source,
          currency: entityPrice.currency,
          percentage: entityPrice.percentage,
        };
      }

      const response = await fetchApi(
        `/api/v2/comments${
          comment ? `/${comment.id}` : ""
        }?include=commenter,attachments${
          !parent
            ? ",commentable,commentable-entities,commentable-insights,primary-insight," +
              "comment-poll,comment-poll.comment-poll-options,comment-prediction"
            : ""
        }`,
        {
          method: comment ? "PATCH" : "POST",
          body: JSON.stringify(serializer.serialize("comments", commentData)),
        }
      );

      if (response.errors) {
        console.error(response);
        Bugsnag.notify("Unable to submit discussion");
        setNotification(
          `Failed to submit comment, ${response.errors[0].title}`,
          "error"
        );
        return;
      }

      if (afterSubmit) {
        afterSubmit(response, { usedSuggestion, usedSuggestionIndex });
        if (blurAfterSubmit) {
          setFocused(false);
        }
      }

      isSentimentGenerated.current = false;
      setValue("");
      setAttachments([]);
      setSentiment(null);
      setMode(null);
      setTargetPrice(null);
      setTargetDate(null);
      setCachedValue("");
      setCachedDraft({});
      setUsedSuggestionIndex(undefined);
      setCachedUsedSuggestionIndex("");
      setPollQuestion(null);
      setPollOptions([]);
      setPollAllowMultipleSelections(false);
      setPollClosesAt(null);

      if (usedSuggestion) {
        setUsedSuggestions([...usedSuggestions, usedSuggestionIndex!]);
      }
    } catch (error: any) {
      Bugsnag.notify(error);
    } finally {
      setIsSubmitting(false);
    }
  }, [
    afterSubmit,
    attachments,
    blurAfterSubmit,
    comment,
    commentable,
    commentableEntities,
    commentableInsights,
    entityPrice?.currency,
    entityPrice?.percentage,
    entityPrice?.price,
    entityPrice?.source,
    isAnonymous,
    isSubmitting,
    loggedInAccount,
    mode,
    parent,
    pollAllowMultipleSelections,
    pollClosesAt,
    pollOptions,
    sentiment,
    setCachedDraft,
    setCachedUsedSuggestionIndex,
    setCachedValue,
    setNotification,
    setUsedSuggestions,
    taggedInsights,
    targetDate,
    targetPrice,
    textValue,
    usedSuggestion,
    usedSuggestionIndex,
    usedSuggestions,
    value,
  ]);

  const profile = useMemo(() => {
    return (
      <ProfileSnippet
        profile={loggedInAccount}
        showCompany
        className="!pl-0"
        alwaysShowAvatar
        useComma
      />
    );
  }, [loggedInAccount]);

  const editor = useMemo(() => {
    if (focused) {
      return (
        <ContentEditor
          ref={editorRef}
          className={twMerge(
            "min-h-[60px] bg-white",
            style === "v3" && "!border-0",
            className
          )}
          editorInputClassName={twMerge(
            style === "v3" &&
              "!h-auto !min-h-[200px] !max-h-[500px] !shadow-none !border-0 !p-0 !text-[14px]",
            mode === "polling" || mode === "prediction" ? "!min-h-[60px]" : ""
          )}
          placeholder={
            mode === "polling"
              ? "Ask a question.."
              : mode === "prediction"
              ? "What's your thesis?"
              : placeholder ||
                `Mention using: ${
                  !loggedInAccount?.isPreviewPass ? "'@' for an Analyst," : ""
                } '$' for an Entity and '#' for an Insight`
          }
          placeholderClassName={twMerge(
            style === "v3" && "!text-[14px] !top-[3px] !left-0"
          )}
          onInput={(value) => {
            setValue(value);
            onChange?.(value);
          }}
          value={value}
          autoFocus={false}
        />
      );
    }

    return (
      <div
        className={twMerge(
          "w-full h-[60px]",
          "border border-solid border-[#ddd] rounded-[2px]",
          "cursor-text",
          "text-[#ccc] py-[15px] px-[10px]",
          "font-roboto text-[14px] leading-[1.3]",
          blurClassName,
          style === "v3" &&
            "!border-0 !rounded-[10px] h-auto min-h-[200px] max-h-[700px]"
        )}
        onClick={() => {
          setFocused(true);
          setTimeout(() => {
            editorRef?.current?.focus();
          }, 100);
        }}
      >
        {placeholder ||
          `Mention using: ${
            !loggedInAccount?.isPreviewPass ? "'@' for an Analyst," : ""
          } '$' for an Entity and '#' for an Insight`}
      </div>
    );
  }, [
    blurClassName,
    className,
    editorRef,
    focused,
    mode,
    loggedInAccount?.isPreviewPass,
    onChange,
    placeholder,
    style,
    value,
  ]);

  const suggestionsSection = useMemo(() => {
    if (loggedInAccount?.isPreviewPass) {
      return null;
    }

    if (!usedSuggestion && !!suggestions?.length) {
      return (
        <div
          className={twMerge(
            hideCommenter || style === "v2" ? "" : "",
            "mt-[15px]",
            "relative"
          )}
        >
          <div className="">
            <div className="">
              {suggestions.map(({ suggestion, index }) => (
                <div
                  key={suggestion}
                  className={twMerge(
                    "my-[5px]",
                    "first-of-type:ml-0 last-of-type:mr-0",
                    "px-[7.5px] py-[2.5px]",
                    "rounded-[15px]",
                    "bg-[rgba(2,118,190,.1)]",
                    "hover:bg-[rgba(2,118,190,.2)]",
                    "border border-solid border-[rgba(2,118,190,.1)] hover:border-[rgba(2,118,190,.2)]",
                    "text-[13px]",
                    "cursor-pointer"
                  )}
                  onClick={() => {
                    setFocused(true);
                    setUsedSuggestionIndex(index);
                    setTimeout(() => {
                      editorRef?.current?.append(suggestion);
                    }, 100);
                  }}
                >
                  {/* eslint-disable-next-line @next/next/no-img-element */}
                  <img
                    src="https://assets.smartkarma.com/icons/sparkle-pink.svg"
                    alt="Quiddity Northbound Monitor"
                    width={20}
                    height={12}
                    className="pb-[2px] mr-[5px]"
                  />
                  {suggestion}
                </div>
              ))}
            </div>
          </div>
        </div>
      );
    }

    return null;
  }, [
    editorRef,
    hideCommenter,
    loggedInAccount?.isPreviewPass,
    style,
    suggestions,
    usedSuggestion,
  ]);

  useEffect(() => {
    setCachedValue(value);
  }, [setCachedValue, value]);

  useEffect(() => {
    setCachedUsedSuggestionIndex(
      typeof usedSuggestionIndex !== "undefined"
        ? usedSuggestionIndex.toString()
        : ""
    );
  }, [setCachedUsedSuggestionIndex, usedSuggestionIndex]);

  useEffect(() => {
    if (prevModeRef.current === mode) return;
    prevModeRef.current = mode;
    if (mode === "polling") {
      setSentiment("neutral");
      setPollClosesAt((prev) => {
        if (prev) return prev;
        const date = new Date();
        date.setDate(date.getDate() + 3);
        return date.toISOString().split("T")[0];
      });
    } else {
      setSentiment(null);
    }
  }, [mode]);

  useEffect(() => {
    setCachedDraft({
      sentiment,
      mode,
      targetPrice,
      targetDate,
      pollQuestion,
      pollAllowMultipleSelections,
      pollOptions,
      pollClosesAt,
      attachments,
    });
  }, [
    sentiment,
    mode,
    targetPrice,
    targetDate,
    pollQuestion,
    pollAllowMultipleSelections,
    pollOptions,
    pollClosesAt,
    attachments,
    setCachedDraft,
  ]);

  useEffectOnce(
    (ack) => {
      if (parent) return;

      if (commentable) {
        if (["entity", "entities"].includes(commentable?.type?.toLowerCase())) {
          setTaggedEntity({
            ...commentable,
          });
        } else if (
          ["insight", "insights"].includes(commentable?.type?.toLowerCase())
        ) {
          setTaggedInsights(commentable);
          const primaryEntity =
            commentable.primaryEntity || commentable["primary-entity"];
          const secondaryEntities =
            commentable.secondaryEntities || commentable["secondary-entities"];

          if (
            primaryEntity &&
            (!secondaryEntities || secondaryEntities?.length < 2)
          ) {
            setTaggedEntity(primaryEntity);
          }
        }

        const primaryInsight =
          comment?.primaryInsight ||
          (
            comment?.commentableInsights ||
            (comment?.["commentable-insights"] as any[]) ||
            []
          ).find((item: any) => item.primary);

        if (primaryInsight) {
          setTaggedInsights(primaryInsight);
        }

        ack();
      }
    },
    [commentable]
  );

  if (!loggedInAccount?.canPostComment) {
    return null;
  }

  if (style === "v3") {
    return (
      <ErrorBoundary
        errorComponent={
          <div className={twMerge("mb-[15px]")}>
            <div className="bg-white rounded-lg shadow-lg flex">
              <div className="w-[47px] rounded-l bg-bearish-red text-white text-[45px] flex items-center justify-center">
                <div>!</div>
              </div>
              <div className="p-[15px] w-[320px] text-left">
                {"Oops! Something went wrong. Please try again."}
              </div>
            </div>
          </div>
        }
        onError={onError}
      >
        <DiscussionEditorV3
          editor={editor}
          inputCommentable={inputCommentable}
          suggestions={suggestionsSection}
          onSetTaggedEntity={setTaggedEntity}
          taggedEntity={taggedEntity}
          sentiment={sentiment}
          setSentiment={setSentiment}
          setAttachments={setAttachments}
          attachments={attachments}
          canSubmit={canSubmit}
          onCancel={onCancel}
          submit={submit}
          isSubmitting={isSubmitting}
          parent={parent}
          isAnonymous={isAnonymous}
          toggleAnonymous={toggleAnonymous}
          mode={mode}
          setMode={setMode}
          targetPrice={targetPrice}
          setTargetPrice={setTargetPrice}
          targetDate={targetDate}
          setTargetDate={setTargetDate}
          pollQuestion={pollQuestion}
          setPollQuestion={setPollQuestion}
          pollOptions={pollOptions}
          setPollOptions={setPollOptions}
          pollAllowMultipleSelections={pollAllowMultipleSelections}
          setPollAllowMultipleSelections={setPollAllowMultipleSelections}
          pollClosesAt={pollClosesAt}
          setPollClosesAt={setPollClosesAt}
          isEditing={!!comment}
          onlyAttachments={onlyAttachments}
          setEntityPrice={setEntityPrice}
        />
      </ErrorBoundary>
    );
  }

  return (
    <ErrorBoundary
      errorComponent={
        <div className={twMerge("mb-[15px]")}>
          <div className="bg-white rounded-lg shadow-lg flex">
            <div className="w-[47px] rounded-l bg-bearish-red text-white text-[45px] flex items-center justify-center">
              <div>!</div>
            </div>
            <div className="p-[15px] w-[320px] text-left">
              {"Oops! Something went wrong. Please try again."}
            </div>
          </div>
        </div>
      }
      onError={onError}
    >
      <div>
        {!hideCommenter && ["default", "v3"].includes(style) && profile}
        <div
          className={twMerge(
            hideCommenter || ["v2", "v3"].includes(style) ? "" : "md:pl-[45px]"
          )}
        >
          {editor}
          {suggestionsSection}
          <div className="mt-[10px] flex justify-between">
            {style === "v2" && !hideCommenter && profile}

            <div className="flex space-x-[10px] md:space-x-[25px]">
              {loggedInAccount?.canPostCommentAnonymously && (
                <div className="hidden md:flex space-x-[5px] py-[7px]">
                  <div className="text-[13px]">Be anonymous?</div>
                  <SwitchButton
                    checked={isAnonymous}
                    onClick={toggleAnonymous}
                  />
                </div>
              )}

              <OverlayTrigger
                overlay={
                  canSubmit ? (
                    <></>
                  ) : (
                    <Tooltip className="hidden md:block">
                      Write or attach something to post
                    </Tooltip>
                  )
                }
              >
                <div className="space-x-[10px] md:space-x-[25px]">
                  {onCancel && (
                    <button
                      className={twMerge(
                        "border border-solid border-active-black",
                        "bg-white hover:bg-active-black",
                        "text-active-black hover:text-white",
                        "text-[13px] font-bold leading-[1]",
                        "rounded-full px-[1.25em] py-[0.75em]"
                      )}
                      onClick={onCancel}
                    >
                      Cancel
                    </button>
                  )}
                  <button
                    className={twMerge(
                      "border-0 ",
                      canSubmit
                        ? "bg-smart-teal hover:bg-dark-teal"
                        : "bg-[#ccc] ",
                      "text-[13px] font-bold leading-[1]",
                      canSubmit ? "text-white" : "text-[#e0e0e0]",
                      "rounded-full px-[1.25em] py-[0.75em]"
                    )}
                    disabled={!canSubmit || isSubmitting}
                    onClick={submit}
                  >
                    {isSubmitting ? (
                      <FontAwesomeIcon
                        icon={faSpinner}
                        className="hidden md:inline-block mr-[5px] animate-spin"
                      />
                    ) : (
                      <FontAwesomeIcon
                        icon={faCommentDots}
                        className="hidden md:inline-block mr-[5px]"
                      />
                    )}
                    Post
                  </button>
                </div>
              </OverlayTrigger>
            </div>
          </div>
          <div className="mt-[10px] flex">
            <div className="flex-1"></div>
            {loggedInAccount?.canPostCommentAnonymously && (
              <div className="flex md:hidden space-x-[5px] py-[7px]">
                <div className="text-[13px]">Be anonymous?</div>
                <SwitchButton checked={isAnonymous} onClick={toggleAnonymous} />
              </div>
            )}
          </div>
        </div>
      </div>
    </ErrorBoundary>
  );
};

export default memo(DiscussionEditor);
